import * as React from "react";
import { Card, CardHeader } from "./primitives";
import {
  CallRow,
  type CallRowProps,
  CheckRow,
  type CheckRowProps,
  HoursBlock,
  type HoursCell,
  ListFooter,
} from "./dashboard-rows";
import { IconChevronRight } from "./icons";
import { SmartLink } from "./link-context";

// ---------------------------------------------------------------------------
// PageHeader
// ---------------------------------------------------------------------------
export interface PageHeaderProps {
  title: React.ReactNode;
  sub?: React.ReactNode;
  meta?: { label: React.ReactNode; value: React.ReactNode; subValue?: React.ReactNode };
  className?: string;
  /**
   * Heading level for the title. Defaults to "h1" for real pages where this is
   * the document's main heading. Decorative dashboard previews embedded inside a
   * marketing page (which already has its own <h1>) should pass "h2" to keep a
   * valid single-<h1> outline. Visual size is unchanged via the .h1 class.
   */
  headingLevel?: "h1" | "h2";
}
export function PageHeader({
  title,
  sub,
  meta,
  className = "",
  headingLevel = "h1",
}: PageHeaderProps) {
  const Title = headingLevel;
  return (
    <header className={["hdr", className].filter(Boolean).join(" ")}>
      <div>
        <Title className="h1">{title}</Title>
        {sub && <p className="sub">{sub}</p>}
      </div>
      {meta && (
        <div className="meta-col">
          <span className="lbl">{meta.label}</span>
          <span className="val">{meta.value}</span>
          {meta.subValue && <span className="sub-val">{meta.subValue}</span>}
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// DashboardHero
// ---------------------------------------------------------------------------
export type HeroState = undefined | "scheduled" | "report";

export interface DashboardHeroProps {
  state?: HeroState;
  cols?: 2 | 3;
  children: React.ReactNode;
  className?: string;
}
export function DashboardHero({ state, cols = 3, children, className = "" }: DashboardHeroProps) {
  return (
    <div className={["hero", className].filter(Boolean).join(" ")} data-state={state}>
      <div className={["hero-grid", cols === 3 && "cols-3"].filter(Boolean).join(" ")}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecentCallLog — BHG
// ---------------------------------------------------------------------------
export interface RecentCallLogProps {
  title?: React.ReactNode;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  rows: CallRowProps[];
  footer?: { summary: React.ReactNode; action?: { label: React.ReactNode; href?: string } };
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  wide?: boolean;
}
export function RecentCallLog({
  title = "Recent calls",
  meta,
  action,
  rows,
  footer,
  pagination,
  wide,
}: RecentCallLogProps) {
  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize))
    : 1;
  return (
    <Card wide={wide}>
      <CardHeader title={title} meta={meta} action={action} />
      <div className="calls">
        {rows.map((r, i) => (
          <CallRow key={i} {...r} />
        ))}
      </div>
      {pagination && totalPages > 1 ? (
        <div
          className="calls-footer list-footer"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <span>
            Page {pagination.page} of {totalPages}
          </span>
          <span style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={pagination.page >= totalPages}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              Next
            </button>
          </span>
        </div>
      ) : footer ? (
        <ListFooter summary={footer.summary} action={footer.action} />
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// RecentChecks — UA variant
// ---------------------------------------------------------------------------
export interface RecentChecksProps {
  title?: React.ReactNode;
  meta?: React.ReactNode;
  rows: CheckRowProps[];
  footer?: { summary: React.ReactNode; action?: { label: React.ReactNode; href?: string } };
  wide?: boolean;
}
export function RecentChecks({
  title = "Recent checks",
  meta,
  rows,
  footer,
  wide,
}: RecentChecksProps) {
  return (
    <Card wide={wide}>
      <CardHeader title={title} meta={meta} />
      <div className="checks">
        {rows.map((r, i) => (
          <CheckRow key={i} {...r} />
        ))}
      </div>
      {footer && <ListFooter summary={footer.summary} action={footer.action} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ClinicSnapshot
// ---------------------------------------------------------------------------
export interface ClinicSnapshotProps {
  title?: React.ReactNode;
  editAction?: { label: React.ReactNode; href?: string };
  name: string;
  addressLines: React.ReactNode[];
  phone?: string;
  hours: HoursCell[];
  link?: { label: React.ReactNode; href?: string };
}
export function ClinicSnapshot({
  title = "Clinic snapshot",
  editAction,
  name,
  addressLines,
  phone,
  hours,
  link,
}: ClinicSnapshotProps) {
  return (
    <Card>
      <CardHeader
        title={title}
        action={
          editAction && <SmartLink href={editAction.href || "#"}>{editAction.label}</SmartLink>
        }
      />
      <div className="clinic">
        <div>
          <h4 className="clinic-name">{name}</h4>
          <p className="clinic-addr">
            {addressLines.map((l, i) => (
              <React.Fragment key={i}>
                {l}
                {i < addressLines.length - 1 && <br />}
              </React.Fragment>
            ))}
            {phone && (
              <>
                <br />
                <span className="ph">{phone}</span>
              </>
            )}
          </p>
        </div>
        <HoursBlock cells={hours} />
        {link && (
          <SmartLink className="clinic-link" href={link.href || "#"}>
            {link.label}
            <IconChevronRight style={{ width: 12, height: 12 }} />
          </SmartLink>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AtAGlance
// ---------------------------------------------------------------------------
export interface AtAGlanceProps {
  title?: React.ReactNode;
  meta?: React.ReactNode;
  children: React.ReactNode;
}
export function AtAGlance({ title = "At a glance", meta, children }: AtAGlanceProps) {
  return (
    <Card>
      <CardHeader title={title} meta={meta} />
      <div className="glance">{children}</div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TopBar
// ---------------------------------------------------------------------------
export interface TopBarProps {
  crumb: { crumbs: string[]; emphasized: React.ReactNode };
  status?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}
export function TopBar({ crumb, status, actions, className = "" }: TopBarProps) {
  return (
    <div className={["top", className].filter(Boolean).join(" ")}>
      <div className="crumb">
        {crumb.crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {c}
            {" / "}
          </React.Fragment>
        ))}
        <b>{crumb.emphasized}</b>
      </div>
      <div className="top-right">
        {status}
        {actions}
      </div>
    </div>
  );
}
